import { RecoverableBinding, recoverableFieldWithOptions } from "../../../ServiceDesk/recovery/recoverable-control";
import { DeviceDetectorService } from "../../../ServiceDesk/sdServices/device-detector-service";
import { UiControlConstants } from "../../common/uicontrol-constants";
import { LocalizationService } from "../../localization/localization";
import { GlobalTelemetryConstants } from "../../logger/telemetry-constants";
import { TelemetryService } from "../../logger/telemetry-service";

export class QuickSelectBindings extends RecoverableBinding {
  /** The list of menu items to bind to. It's expected that this list will conform to the `QuickSelectItem {displayName: string, isSelected?: boolean}` class type.
   * There are also a number of features that are accessible via this object through the propery on the menuItem object `$sd` after the initialized callback has been triggered,
   * see QuickSelectDelegateObject for details, usage would be `menuItems.$sd.refresh();`
   * Callers can use the interface `QuickSelectMenuItemCollection` to represent this collection to have strongely typed references to the supported delegate features.
   */
  public menuItems = "<";

  /** Callback raised when the control has been initalized, this is required to access any menuItem extensions */
  public initialized = "&?";

  /** Callback raised when a menu item is selected. The callback argument is a { selectedItems: QuickSelectItem[], selectedAll: boolean, clearedAll: boolean } */
  public onSelectionChanged = "&?";

  /** Callback raised when a menu item is selected. But before the control manipulate the data */
  public onSelectionChangeStart = "&?";

  /** Called to set any initial selection state on the menu items.
   *  Parameter '{ QuickSelectItem: any}' is passed to the function.
   *  Required when there is a need to set the initial load for area path.
   */
  public initialSelectionPredicate = "&?";

  /** Placeholder text to display when no items are selected, defaults to Select an option. */
  public placeholderText = "@?";

  /** Label of the input control. */
  public label = "@?";

  /** aria-label of the Unordered List control. */
  public unorderedListAriaLabel = "@?";

  /** Callback raised when the drop down menu is opened or closed. The callback argument is { isOpen: boolean }. */
  public onDropDownChanged = "&?";

  /** The identifier for all business intelligence (BI) actions triggered by this component.*/
  public biId = "@?";

  /** The maximum number of items to display, defaults to 8 */
  public dropDownMaxItemCount = "@?";

  /** Shows the bulk options under the overlay menu, defaults to false */
  public showBulkOptions = "@?";

  /** Which selection mode the control is operating in, default is Multiple, other choice is Single */
  public selectionMode = "@?";

  /** Is the control disabled, defaults to false */
  public isDisabled = "<?";

  /** sets the required attribute on the control */
  public isRequired = "@?";

  /** sets the required attribute on the control */
  public showLabelIsRequired = "@?";

  /** sets the required attribute on the control */
  public hidelabel = "@?";

  /**scenario for awa telemetry logging */
  public scenario = "<?";

  public name = "@?";
  public ariaLabelledby = "@?";

  public ariaLabel = "@?";
}

const selectionModeMultiple = "Multiple";
const selectionModeSingle = "Single";
export type SelectionMode = "Single" | "Multiple";

/** Menu items for the quick select control */
export interface QuickSelectItem {
  /** The string to display to the user and to filter on */
  displayName: string;
  /** If this item is selected */
  isSelected?: boolean;
  /** The unique id for this item. Required if using recovery. */
  id?: string;
}

/** Exported only to make ts lint happy with the public methods here. */
export interface QuickSelectItemInternal extends QuickSelectItem {
  /** If this item is focused in the list of options, cannot be set by callers */
  isFocused?: boolean;
  /** If this item is shown in the list of options, cannot be set by callers */
  isShown?: boolean;
}

/** The results of the on onSelectionChanged callback */
export interface QuickSelection {
  /** The list of all selected items during this change */
  selectedItems: QuickSelectItem[];
  /** If selected all was used for this change */
  selectedAll?: boolean;
  /** If clear all was used for this change */
  clearedAll?: boolean;
}

export interface QuickSelectMenuItemCollection extends Array<QuickSelectItem> {
  $sd?: QuickSelectDelegateObject;
}

/** Exposed functionality for quick select consumers */
export interface QuickSelectDelegateObject {
  /** Refreshes the state of the quick select control, fire this after updating any internal state of menu items to redraw the menu and preview text. */
  refresh: () => void;
  /** Clears the current search term */
  clearSearch?: () => void;
}
export class QuickSelectController {
  public menuItems: QuickSelectItemInternal[];
  public initialized: () => void;
  public onSelectionChangeStart: () => void;
  public onSelectionChanged: (arg: QuickSelection) => void;
  public initialSelectionPredicate: (arg: QuickSelectItem) => boolean;
  public placeholderText: string;
  public label: string;
  public showLabelIsRequired: boolean;
  public hidelabel: boolean;
  public onDropDownChanged: (arg: { isOpen: boolean }) => void;
  public biId: string;
  public name: string;

  public dropDownMaxItemCount = 8;
  public dropDownMinItemCount = 1;

  public showBulkOptions = false;
  public selectionMode: SelectionMode;
  public isDisabled: boolean;
  public scrollToIndex: number;

  public showDropDown = false;
  public searchText: string;
  @recoverableFieldWithOptions({ preventAutomaticRecovery: true })
  private selectedItems: QuickSelectItem[] = [];
  private focusedItem: QuickSelectItem;
  private separatorText: string;
  private defaultPlaceholderText: string;
  private lastSearchText: string;
  private lastFilteredMenu: QuickSelectItemInternal[] = [];
  private lastSearchTerm: string;
  private defaultScrollToIndex = 0;
  private showWholeList = false;
  private isInvalid = false;
  private isDirty = false;
  public isFocused = false;
  private scenario: string;
  private unorderedListAriaLabel: string;
  private IsFocus = false;
  // @ngInject
  constructor(
    private $element: ng.IAugmentedJQuery,
    private $scope: ng.IScope,
    private sdLocalizationService: LocalizationService,
    private $interval: ng.IIntervalService,
    public deviceDetector: DeviceDetectorService,
    public telemetryService: TelemetryService,
    public telemetryConstants: GlobalTelemetryConstants,
    public uiControlConstants: UiControlConstants
  ) {
    this.separatorText = sdLocalizationService.getLocalizedString("ShellCommon", "_CONTROLS_QUICK_SELECT_SEPARATOR_");
    this.defaultPlaceholderText = sdLocalizationService.getLocalizedString(
      "ShellCommon",
      "_CONTROLS_QUICK_SELECT_DEFAULT_PLACEHOLDER_"
    );
    $scope.$watch(
      () => {
        return $element.find("input")[0].classList;
      },
      (newValue: any) => {
        this.isInvalid = newValue.contains("ng-invalid");
        this.isDirty = newValue.contains("ng-dirty");
      }
    );
  }

  $onInit() {
    if (!this.selectionMode) {
      this.selectionMode = selectionModeMultiple;
    }

    if (angular.isFunction(this.initialSelectionPredicate)) {
      this.menuItems.forEach((i) => (i.isSelected = this.initialSelectionPredicate(i)));
    }

    this.refresh();

    if (this.placeholderText === undefined) {
      this.placeholderText = this.defaultPlaceholderText;
    }

    if (this.label === undefined) {
      this.label = this.placeholderText;
      this.hidelabel = true;
    }

    if (this.unorderedListAriaLabel === undefined) {
      this.unorderedListAriaLabel = "Choose an element";
    }

    this.attachDelegateObjectToMenuItems();

    if (angular.isFunction(this.initialized)) {
      this.initialized();
    }
  }

  $onChanges(changesObj: any) {
    if (changesObj.menuItems || changesObj.isDisabled) {
      this.attachDelegateObjectToMenuItems();
      this.refresh();
    }
  }

  $sdRecoveryPreserialize(key: string, selectedItems: QuickSelectItem[]) {
    if (selectedItems) {
      return selectedItems.map((i) => i.id);
    }
    return undefined;
  }

  public $sdRecoveryIsModelValid(data: any) {
    if (data.selectedItems && data.selectedItems.length > 0) {
      let allFound = true;
      data.selectedItems.forEach((recoveredSelectedId: string) => {
        allFound = allFound && !!this.menuItems.find((i) => i.id == recoveredSelectedId);
      });
      return allFound;
    }
    return false;
  }

  public $sdRecoveryOnComplete(data: any) {
    let firstSelection: QuickSelectItem = undefined;
    this.menuItems.forEach((i) => {
      i.isSelected = !!data.selectedItems.find((s: string) => s == i.id);
      if (i.isSelected) {
        firstSelection = firstSelection || i;
      }
    });
    this.focusedItem = firstSelection;
    this.refresh();
  }

  private attachDelegateObjectToMenuItems() {
    let delegateObject: QuickSelectDelegateObject = {
      refresh: this.refresh.bind(this),
      clearSearch: this.clearSearch.bind(this)
    };
    angular.merge(this.menuItems, { $sd: delegateObject });
  }

  public refresh(): void {
    this.lastFilteredMenu = undefined;
    let selectedItems = this.getSelectedItems();
    this.selectedItems = selectedItems;
    if (this.selectionMode === selectionModeMultiple) {
      this.searchText = selectedItems.map((item) => item.displayName).join(this.separatorText);
      if (selectedItems.length > 0) {
        this.searchText += this.separatorText + " ";
      }
      if (this.lastSearchTerm) {
        this.searchText += this.lastSearchTerm;
      }
    } else {
      this.searchText = this.selectedItems.length > 0 ? this.selectedItems[0].displayName : "";
      this.showWholeList = true;
    }
  }

  private onSelected(item: QuickSelectItem) {
    if (this.selectionMode == selectionModeSingle) {
      this.menuItems.forEach((i) => (i.isSelected = item === i));
      this.showWholeList = true;
      this.searchText = item.displayName;
      this.toggleDropDownVisibility(false);
    }

    this.refresh();
  }

  private getSelectedItems(): QuickSelectItem[] {
    return this.menuItems.filter((item) => item.isSelected);
  }

  public selectAll() {
    if (angular.isFunction(this.onSelectionChangeStart)) {
      this.onSelectionChangeStart();
    }

    this.menuItems.forEach((item) => {
      item.isSelected = true;
      // Fire the individual selection for back compatibility with the multi-select control
      this.onSelected(item);
    });
    this.searchText = "All items selected";
    this.selectedItems = this.getSelectedItems();
    if (angular.isFunction(this.onSelectionChanged)) {
      this.onSelectionChanged({ selectedItems: this.getSelectedItems(), selectedAll: true });
    }
  }

  public clearSelection() {
    if (angular.isFunction(this.onSelectionChangeStart)) {
      this.onSelectionChangeStart();
    }
    this.menuItems.forEach((item) => (item.isSelected = false));
    this.searchText = undefined;
    this.selectedItems = this.getSelectedItems();
    if (angular.isFunction(this.onSelectionChanged)) {
      this.onSelectionChanged({ selectedItems: this.getSelectedItems(), clearedAll: true });
    }
    this.$element.find(".item-input-field").focus();
  }

  public clearSearch() {
    this.searchText = "";
    this.lastSearchTerm = "";
    this.lastSearchText = "";
    this.scrollToIndex = this.defaultScrollToIndex;
    this.setFocusPosition(this.scrollToIndex);
  }

  public activateSelection(item: QuickSelectItemInternal) {
    this.telemetryService.pushPageActionEvent(
      this.telemetryConstants.pageActionType.click,
      this.telemetryConstants.pageActionBehavior.Unknown,
      {
        scn: this.scenario,
        scnstp: this.scenario + ".quickSelect"
      },
      {
        item: item.displayName
      }
    );
    if (angular.isFunction(this.onSelectionChangeStart)) {
      this.onSelectionChangeStart();
    }
    this.focusedItem = this.menuItems.find((i) => i === item);
    if (this.selectionMode == selectionModeSingle) {
      this.toggleSelection(item, true);
    } else {
      this.toggleSelection(item);
    }
    this.selectedItems = this.getSelectedItems();
    if (angular.isFunction(this.onSelectionChanged)) {
      this.onSelectionChanged({ selectedItems: this.getSelectedItems() });
    }
  }

  private toggleSelection(item: QuickSelectItem, value?: boolean) {
    if ((item.isSelected = value !== undefined ? value : !item.isSelected)) {
      this.onSelected(item);
    } else {
      this.refresh();
    }
  }

  public toggleDropDownVisibility(value?: boolean) {
    let beforeValue = this.showDropDown;
    this.showDropDown = value !== undefined ? value : !this.showDropDown;
    this.isFocused = this.showDropDown;

    // Only call onDropDownChanged when the value was changed
    if (angular.isFunction(this.onDropDownChanged) && beforeValue !== this.showDropDown) {
      this.onDropDownChanged({ isOpen: this.showDropDown });
    }
  }

  public fullDropDownHeight(): number {
    let bulkOptionsHeight = this.$element.find(".bulk-options").outerHeight(); // If not visible this will come back as zero
    let itemHeight = this.$element.find(".sd-menu-item").outerHeight();
    let borderWidth = 2;

    return (
      Math.max(this.countOfItemsToShow(), this.dropDownMinItemCount) * itemHeight + borderWidth + bulkOptionsHeight
    );
  }

  public itemOnlyDropDownHeight(): number {
    let itemHeight = this.itemHeight();
    return this.countOfItemsToShow() * itemHeight;
  }

  public itemHeight(): number {
    return this.$element.find(".sd-menu-item").outerHeight();
  }

  public countOfItemsToShow(): number {
    let showableItemCount = 0;
    for (let i = 0; i < this.menuItems.length && showableItemCount < this.dropDownMaxItemCount; ++i) {
      if (this.menuItems[i].isShown != false) {
        showableItemCount++;
      }
    }
    return showableItemCount;
  }

  public keypress($event: KeyboardEvent) {
    let ele = <HTMLElement>event.target;
    let keyCode = $event.keyCode;
    switch (keyCode) {
      case this.uiControlConstants.keyCode.tabKeyCode:
        // Let the browser handle tabbing
        return;
      case this.uiControlConstants.keyCode.leftArrowKeyCode:
      case this.uiControlConstants.keyCode.rightArrowKeyCode:
        // Do not prevent any arrow key usage
        return;

      case this.uiControlConstants.keyCode.enterKeyCode:
        if (!this.showDropDown) {
          this.toggleDropDownVisibility(true);
        } else {
          this.activateSelection(this.menuItems.find((i) => i === this.focusedItem));
        }
        ele.removeAttribute("aria-activedescendant");
        event.preventDefault();
        return;
      case this.uiControlConstants.keyCode.pageUpKeyCode:
        // For loop is required so we don't skip past end of virtualized rows
        for (let i = 0; i < this.dropDownMaxItemCount; i++) {
          this.moveFocus(-1, ele);
        }
        event.preventDefault();
        return;
      case this.uiControlConstants.keyCode.upArrowKeyCode:
            this.moveFocus(-1, ele);
            this.IsFocus = true;
        event.preventDefault();
        return;
      case this.uiControlConstants.keyCode.downArrowKeyCode:
            this.moveFocus(1, ele);
            this.IsFocus = true;
        event.preventDefault();
        return;
      case this.uiControlConstants.keyCode.pageDownKeyCode:
        // For loop is required so we don't skip past end of virtualized rows
        for (let i = 0; i < this.dropDownMaxItemCount; i++) {
          this.moveFocus(1, ele);
        }
        event.preventDefault();
        return;
      case this.uiControlConstants.keyCode.escapeKeyCode:
        this.toggleDropDownVisibility(false);
        event.preventDefault();
        return;
      default:
        this.toggleDropDownVisibility(true);
        this.showWholeList = false;
    }
  }

  public focusInput() {
    this.$element.find(".item-input-field").focus();
    this.toggleDropDownVisibility(true);
  }

  public focusItem(item: QuickSelectItem) {
    let focusIndex = this.getFocusPositionOfItem(item);
    this.setFocusPosition(focusIndex);
  }

  private moveFocus(amount: number, ele: HTMLElement) {
    let focusIndex: number;
    if (this.menuItems.length <= 0) {
      return;
    } else if (this.focusedItem === undefined && Math.abs(amount) === 1) {
      focusIndex = 0;
    } else {
      focusIndex = this.getFocusPositionOfItem(this.focusedItem) + amount;
    }
    this.setFocusPosition(focusIndex);
    this.toggleDropDownVisibility(true);
  }

  private getFocusPositionOfItem(item: QuickSelectItem) {
    for (let i = 0; i <= this.lastFilteredMenu.length; i++) {
      if (this.lastFilteredMenu[i] === item) {
        return i;
      }
    }
    return -1;
  }

  private setFocusPosition(position: number) {
    if (position < 0) {
      position = 0;
    }
    let setFocus = false;
    for (let i = 0; i < this.lastFilteredMenu.length; i++) {
      if (i == position || (!setFocus && i == this.lastFilteredMenu.length - 1)) {
        this.focusedItem = this.lastFilteredMenu[i];
        this.lastFilteredMenu[i].isFocused = true;
        setFocus = true;
      } else {
        this.lastFilteredMenu[i].isFocused = false;
      }
    }
  }

  public hasShownItems() {
    return !this.menuItems.every((o: QuickSelectItemInternal) => o.isShown == false);
  }

  public shouldShow(item: QuickSelectItemInternal, searchTerm: string): boolean {
    if (!searchTerm || this.showWholeList) {
      return (item.isShown = true);
    }

    if (!item.displayName) {
      return (item.isShown = false);
    }

    let searchTextParts = searchTerm.toLowerCase().split(" ");
    let displayNameLower = item.displayName.toLowerCase();
    return (item.isShown = searchTextParts.every((part) => displayNameLower.includes(part)));
  }

  private getSearchTermPortionOfSearchText(searchText: string): string {
    if (!searchText) {
      return undefined;
    }

    let searchTextSeparatedParts = searchText.split(this.separatorText);
    return searchTextSeparatedParts[searchTextSeparatedParts.length - 1].trim();
  }

  public filteredMenuItems() {
    if (
      this.lastFilteredMenu === undefined ||
      this.lastFilteredMenu.length === 0 ||
      this.lastSearchText != this.searchText
    ) {
      let isRefresh: boolean = this.lastFilteredMenu === undefined || this.lastFilteredMenu.length === 0;
      let searchTerm =
        this.selectionMode == selectionModeSingle
          ? this.searchText
          : this.getSearchTermPortionOfSearchText(this.searchText);
      this.lastSearchTerm = searchTerm;
      this.lastFilteredMenu = this.menuItems.filter((i) => this.shouldShow(i, searchTerm));
      if (isRefresh) {
        this.scrollToIndex = this.lastFilteredMenu.findIndex((i) => i === this.focusedItem);
      } else {
        this.scrollToIndex = this.defaultScrollToIndex;
      }
      this.setFocusPosition(this.scrollToIndex);
      }
      if (this.IsFocus == true) {
          this.searchText = this.focusedItem.displayName;
      }
      this.IsFocus == false;
      this.lastSearchText = this.searchText;
    return this.lastFilteredMenu;
  }
}

export const sdQuickSelect: ng.IComponentOptions = {
  bindings: <any>new QuickSelectBindings(),
  controller: QuickSelectController,
  templateUrl: "../ShellCommon/Controls/quick-select/quick-select.html"
};
